import * as React from "react";

import { filter } from "graphql-anywhere";
import { getIconForCustomizationAreaTemplate } from "../../helpers/getIconForCustomizationAreaTemplate";
import { getIconForProductionJobMethod } from "../../helpers/getIconForProductionJobMethod";
import { getProductionMethodOptionAvailability } from "../../helpers/getProductionMethodOptionAvailability";
import { fragments as inkColorDetailsFragments } from "../InkColorDetails/graph";
import { makeStyles } from "@mui/styles";
import { updateOrderProductionJobCustomizationAreaColorsMutation } from "../../graph";
import { useMemo, useState } from "react";
import { useMutation } from "@apollo/client";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import CustomizationAreaRadioList from "./components/CustomizationAreaRadioList";
import FormControl from "@mui/material/FormControl";
import FormControlLabel from "@mui/material/FormControlLabel";
import InkColorDetails from "../InkColorDetails";
import InkColorModal from "../InkColorModal";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import VinylConfigurationSelector from "./components/VinylConfigurationSelector";
import withSnackbar from "../../components/withSnackbar";

const useStyles = makeStyles(theme => ({
  addColorsButton: { marginBottom: theme.spacing(2) },

  colorCount: {
    marginBottom: theme.spacing(2)
  },

  printAreaMethodAttributesContainer: {
    display: "flex",
    marginBottom: theme.spacing(2)
  },

  areaContainer: {
    flex: 1
  },

  methodContainer: {
    flex: 1
  },

  list: {
    listStyle: "none",
    marginTop: 0,
    marginBottom: theme.spacing(2),
    paddingLeft: theme.spacing(1)
  },

  listItem: {
    marginBottom: theme.spacing(1)
  },

  attributesContainer: {
    flex: 0.6
  },

  inkColorContainer: {
    display: "flex",
    flexDirection: "column"
  },

  splitIntoColorsCheckbox: {
    width: "150px"
  }
}));

const defaultInkColorInputErrors = {
  colors: [],
  colorsInput: {
    inkColorId: [],
    pantoneColorId: []
  },
  orderProductionJobCustomizationAreaInkColorId: []
};

const CustomizationAreaForm = ({
  availableCustomizationAreaMethods,
  availableCustomizationAreaTemplates,
  inputErrors,
  inputErrors: {
    autobase: autobaseErrors,
    colorCount: colorCountErrors,
    distressed: distressedErrors,
    flatten: flattenErrors,
    halftones: halftonesErrors,
    imageRepair: imageRepairErrors,
    recreate: recreateErrors,
    sponsorback: sponsorbackErrors,
    vinylConfiguration: vinylConfigurationErrors
  },
  vinylColors,
  vinylFonts,
  vinylConfigurationInputErrors,
  customizationArea,
  customizationArea: {
    id,
    autobase,
    distressed,
    flatten,
    halftones,
    imageRepair,
    inkColors,
    method,
    recreate,
    splitIntoColors,
    sponsorback,
    template,
    vinylConfiguration
  },
  onCustomizationAreaUpdated,
  showSuccessSnackbar
}) => {
  const classes = useStyles();

  // Mutations:
  const [updateOrderProductionJobCustomizationAreaColors] = useMutation(
    updateOrderProductionJobCustomizationAreaColorsMutation
  );

  // States:
  const [colorSelections, setColorSelections] = useState(inkColors);
  const [isAddingColors, setIsAddingColors] = useState(false);
  const [inkColorSelectionInputErrors, setInkColorSelectionInputErrors] =
    useState(defaultInkColorInputErrors);
  const [
    customizationAreaTemplateSelectedRadioValue,
    setCustomizationAreaTemplateSelectedRadioValue
  ] = useState(template.id);
  const [
    productCustomizationMethodComboboxSelection,
    setProductCustomizationMethodComboboxSelection
  ] = useState(method);
  const [distressedCheckboxChecked, setDistressedCheckboxChecked] =
    useState(distressed);
  const [halftonesCheckboxChecked, setHalftonesCheckboxChecked] =
    useState(halftones);
  const [flattenCheckboxChecked, setFlattenCheckboxChecked] = useState(flatten);
  const [recreateCheckboxChecked, setRecreateCheckboxChecked] =
    useState(recreate);
  const [sponsorbackCheckboxChecked, setSponsorbackCheckboxChecked] =
    useState(sponsorback);
  const [splitIntoColorsCheckboxChecked, setSplitIntoColorsCheckboxChecked] =
    useState(splitIntoColors);
  const [autobaseCheckboxChecked, setAutobaseCheckboxChecked] =
    useState(autobase);
  const [imageRepairCheckboxChecked, setImageRepairCheckboxChecked] =
    useState(imageRepair);

  const vinylConfigurationToUpdate = useMemo(() => {
    return {
      names: !!(
        vinylConfiguration &&
        (vinylConfiguration.namesColor || vinylConfiguration.namesFont)
      ),
      numbers: !!(
        vinylConfiguration &&
        (vinylConfiguration.numbersColor || vinylConfiguration.numbersFont)
      ),
      size: vinylConfiguration ? vinylConfiguration.size : 8,
      namesColorId:
        vinylConfiguration && vinylConfiguration.namesColor
          ? vinylConfiguration.namesColor.id
          : null,
      namesFontId:
        vinylConfiguration && vinylConfiguration.namesFont
          ? vinylConfiguration.namesFont.id
          : null,
      numbersColorId:
        vinylConfiguration && vinylConfiguration.numbersColor
          ? vinylConfiguration.numbersColor.id
          : null,
      numbersFontId:
        vinylConfiguration && vinylConfiguration.numbersFont
          ? vinylConfiguration.numbersFont.id
          : null
    };
  }, [vinylConfiguration]);

  const availableOptions = useMemo(() => {
    const selectedMethod =
      productCustomizationMethodComboboxSelection === ""
        ? customizationArea.method
        : productCustomizationMethodComboboxSelection;

    const {
      autobaseOptionIsAvailable,
      colorOptionIsAvailable,
      distressedOptionIsAvailable,
      flattenOptionIsAvailable,
      halftonesOptionIsAvailable,
      imageRepairOptionIsAvailable,
      recreateOptionIsAvailable,
      sponsorbackOptionIsAvailable,
      isCutVinyl
    } = getProductionMethodOptionAvailability(selectedMethod);

    return {
      autobaseOptionIsAvailable,
      colorOptionIsAvailable,
      distressedOptionIsAvailable,
      flattenOptionIsAvailable,
      halftonesOptionIsAvailable,
      imageRepairOptionIsAvailable,
      isCutVinyl,
      recreateOptionIsAvailable,
      sponsorbackOptionIsAvailable
    };
  }, [productCustomizationMethodComboboxSelection, customizationArea]);

  // Handlers:
  const addColorsSelection = newColorSelections =>
    setColorSelections([...newColorSelections]);

  const handleCustomizationAreaTemplateRadioButtonSelect =
    customizationAreaTemplateId => {
      setCustomizationAreaTemplateSelectedRadioValue(
        customizationAreaTemplateId
      );
      onCustomizationAreaUpdated({
        template: { id: customizationAreaTemplateId }
      });
    };

  const handleAddColorsButtonClick = () => setIsAddingColors(true);

  const handleAddColorsModalRequestClose = () => setIsAddingColors(false);

  const handleInkColorSelectionUpdate = (inkColors, pantoneColors) => {
    setIsAddingColors(true);
    addColorsSelection([
      ...inkColors.map(inkColor => ({
        inkColor
      })),
      ...pantoneColors.map(pantoneColor => ({
        pantoneColor
      }))
    ]);

    updateOrderProductionJobCustomizationAreaColors({
      variables: {
        colors: [
          ...inkColors.map(({ id }) => ({
            inkColorId: id
          })),
          ...pantoneColors.map(({ id }) => ({
            pantoneColorId: id
          }))
        ],
        orderProductionJobCustomizationAreaId: id
      }
    })
      .then(
        ({
          data: {
            updateOrderProductionJobCustomizationAreaColors: {
              succeeded,
              errors
            }
          }
        }) => {
          succeeded
            ? showSuccessSnackbar("Colors successfully updated")
            : setInkColorSelectionInputErrors(...errors);
        }
      )
      .catch(error => {
        console.log(error);
      })
      .finally(() => setIsAddingColors(false));
  };

  const handleProductCustomizationMethodComboboxSelect = method => {
    setProductCustomizationMethodComboboxSelection(method);
    const {
      autobaseOptionIsAvailable,
      distressedOptionIsAvailable,
      flattenOptionIsAvailable,
      halftonesOptionIsAvailable,
      imageRepairOptionIsAvailable,
      recreateOptionIsAvailable,
      sponsorbackOptionIsAvailable,
      isCutVinyl
    } = getProductionMethodOptionAvailability(method);
    onCustomizationAreaUpdated({
      method,
      vinylConfiguration: isCutVinyl ? vinylConfigurationToUpdate : null,
      autobase: autobaseOptionIsAvailable && autobaseCheckboxChecked,
      distressed: distressedOptionIsAvailable && distressedCheckboxChecked,
      flatten: flattenOptionIsAvailable && flattenCheckboxChecked,
      halftones: halftonesOptionIsAvailable && halftonesCheckboxChecked,
      imageRepair: imageRepairOptionIsAvailable && imageRepairCheckboxChecked,
      recreate: recreateOptionIsAvailable && recreateCheckboxChecked,
      sponsorback: sponsorbackOptionIsAvailable && sponsorbackCheckboxChecked
    });
  };

  const handleDistressedCheckboxChange = event => {
    setDistressedCheckboxChecked(event.target.checked);
    onCustomizationAreaUpdated({ distressed: event.target.checked });
  };

  const handleHalftonesCheckboxChange = event => {
    setHalftonesCheckboxChecked(event.target.checked);
    onCustomizationAreaUpdated({ halftones: event.target.checked });
  };

  const handleFlattenCheckboxChange = event => {
    setFlattenCheckboxChecked(event.target.checked);
    onCustomizationAreaUpdated({ flatten: event.target.checked });
  };

  const handleRecreateCheckboxChange = event => {
    setRecreateCheckboxChecked(event.target.checked);
    onCustomizationAreaUpdated({ recreate: event.target.checked });
  };

  const handleSponsorbackCheckboxChange = event => {
    setSponsorbackCheckboxChecked(event.target.checked);
    onCustomizationAreaUpdated({ sponsorback: event.target.checked });
  };

  const handleSplitIntoColorsCheckboxChange = checked => {
    setSplitIntoColorsCheckboxChecked(checked);
  };

  const handleAutobaseCheckboxChange = event => {
    setAutobaseCheckboxChecked(event.target.checked);
    onCustomizationAreaUpdated({ autobase: event.target.checked });
  };

  const handleImageRepairCheckboxChange = event => {
    setImageRepairCheckboxChecked(event.target.checked);
    onCustomizationAreaUpdated({ imageRepair: event.target.checked });
  };

  const handleVinylConfigurationSelectorUpdated = update => {
    Object.assign(vinylConfigurationToUpdate, update);
    onCustomizationAreaUpdated({
      vinylConfiguration: vinylConfigurationToUpdate
    });
  };

  const handleColorCountInputChange = event => {
    const colorCount = event.target.value;
    onCustomizationAreaUpdated({ colorCount: parseInt(colorCount, 10) });
  };

  return (
    <React.Fragment>
      <div className={classes.printAreaMethodAttributesContainer}>
        <div className={classes.areaContainer}>
          <Typography variant="subtitle2">Print Area</Typography>
          <CustomizationAreaRadioList
            iconMethod={getIconForCustomizationAreaTemplate}
            options={
              availableCustomizationAreaTemplates &&
              availableCustomizationAreaTemplates.map(
                ({ id, description }) => ({
                  id,
                  label: description,
                  icon: description
                })
              )
            }
            onSelect={handleCustomizationAreaTemplateRadioButtonSelect}
            selectionId={customizationAreaTemplateSelectedRadioValue}
          />
        </div>
        <div className={classes.methodContainer}>
          <Typography variant="subtitle2">Print Method</Typography>
          <CustomizationAreaRadioList
            iconMethod={getIconForProductionJobMethod}
            options={
              availableCustomizationAreaMethods &&
              availableCustomizationAreaMethods.map(({ id, label }) => ({
                id,
                label,
                icon: id
              }))
            }
            onSelect={handleProductCustomizationMethodComboboxSelect}
            selectionId={productCustomizationMethodComboboxSelection}
          />
        </div>
        <div className={classes.attributesContainer}>
          <Typography variant="subtitle2">Attributes</Typography>
          <FormControl>
            {availableOptions.distressedOptionIsAvailable && (
              <FormControlLabel
                error={distressedErrors.join(", ")}
                control={
                  <Checkbox
                    checked={distressedCheckboxChecked}
                    onChange={handleDistressedCheckboxChange}
                    color="primary"
                  />
                }
                label="Distressed"
              />
            )}
            {availableOptions.halftonesOptionIsAvailable && (
              <FormControlLabel
                error={halftonesErrors.join(", ")}
                control={
                  <Checkbox
                    checked={halftonesCheckboxChecked}
                    onChange={handleHalftonesCheckboxChange}
                    color="primary"
                  />
                }
                label="Halftones"
              />
            )}
            {availableOptions.flattenOptionIsAvailable && (
              <FormControlLabel
                error={flattenErrors.join(", ")}
                control={
                  <Checkbox
                    checked={flattenCheckboxChecked}
                    onChange={handleFlattenCheckboxChange}
                    color="primary"
                  />
                }
                label="Flatten"
              />
            )}
            {availableOptions.recreateOptionIsAvailable && (
              <FormControlLabel
                error={recreateErrors.join(", ")}
                control={
                  <Checkbox
                    checked={recreateCheckboxChecked}
                    onChange={handleRecreateCheckboxChange}
                    color="primary"
                  />
                }
                label="Recreate"
              />
            )}
            {availableOptions.sponsorbackOptionIsAvailable && (
              <FormControlLabel
                error={sponsorbackErrors.join(", ")}
                control={
                  <Checkbox
                    checked={sponsorbackCheckboxChecked}
                    onChange={handleSponsorbackCheckboxChange}
                    color="primary"
                  />
                }
                label="Sponsorback"
              />
            )}
            {availableOptions.autobaseOptionIsAvailable && (
              <FormControlLabel
                error={autobaseErrors.join(", ")}
                control={
                  <Checkbox
                    checked={autobaseCheckboxChecked}
                    onChange={handleAutobaseCheckboxChange}
                    color="primary"
                  />
                }
                label="Autobase"
              />
            )}
            {availableOptions.imageRepairOptionIsAvailable && (
              <FormControlLabel
                error={imageRepairErrors.join(", ")}
                control={
                  <Checkbox
                    checked={imageRepairCheckboxChecked}
                    onChange={handleImageRepairCheckboxChange}
                    color="primary"
                  />
                }
                label="ImageRepair"
              />
            )}
          </FormControl>
        </div>
      </div>
      {availableOptions.isCutVinyl && vinylConfigurationToUpdate ? (
        <React.Fragment>
          {vinylConfigurationErrors && vinylConfigurationErrors.length > 0 && (
            <Typography color="error">
              {`The vinyl configuration ${vinylConfigurationErrors.join(
                ", "
              )}.`}
            </Typography>
          )}
          <VinylConfigurationSelector
            onVinylConfigurationUpdated={
              handleVinylConfigurationSelectorUpdated
            }
            vinylConfiguration={vinylConfigurationToUpdate}
            vinylConfigurationInputErrors={vinylConfigurationInputErrors}
            vinylColors={vinylColors.map(vinylColor => ({
              color: vinylColor.inkColor.color,
              id: vinylColor.id,
              label: vinylColor.inkColor.name,
              value: vinylColor.id
            }))}
            vinylFonts={vinylFonts.map(vinylFont => ({
              id: vinylFont.id,
              label: vinylFont.alias,
              value: vinylFont.id
            }))}
          />
        </React.Fragment>
      ) : availableOptions.colorOptionIsAvailable ? (
        <React.Fragment>
          <div className={classes.inkColorContainer}>
            <Typography variant="subtitle2">Colors</Typography>
            <FormControlLabel
              className={classes.splitIntoColorsCheckbox}
              error={inputErrors.splitIntoColors.join(", ")}
              control={
                <Checkbox
                  checked={splitIntoColorsCheckboxChecked}
                  onChange={() =>
                    handleSplitIntoColorsCheckboxChange(
                      !splitIntoColorsCheckboxChecked
                    )
                  }
                  color="primary"
                />
              }
              label="Split into colors"
            />
          </div>
          {splitIntoColorsCheckboxChecked ? (
            <React.Fragment>
              {colorSelections.length > 0 && (
                <ul className={classes.list}>
                  {colorSelections.map(({ inkColor, pantoneColor }, index) => (
                    <li className={classes.listItem} key={index}>
                      <InkColorDetails
                        inkColor={
                          inkColor
                            ? filter(
                                inkColorDetailsFragments.inkColor,
                                inkColor
                              )
                            : null
                        }
                        pantoneColor={
                          pantoneColor
                            ? filter(
                                inkColorDetailsFragments.pantoneColor,
                                pantoneColor
                              )
                            : null
                        }
                      />
                    </li>
                  ))}
                </ul>
              )}
              <Button
                className={classes.addColorsButton}
                color="secondary"
                variant="contained"
                onClick={handleAddColorsButtonClick}
              >
                {colorSelections.length > 0 ? "Update Colors" : "Add Colors"}
              </Button>
              <InkColorModal
                inputErrors={inkColorSelectionInputErrors}
                isOpen={isAddingColors}
                onRequestClose={handleAddColorsModalRequestClose}
                onColorsAdded={handleInkColorSelectionUpdate}
                title="Add Ink Colors"
              />
            </React.Fragment>
          ) : (
            <div>
              <TextField
                error={colorCountErrors.join(", ")}
                className={classes.colorCount}
                id="Color Count Input"
                label="Color Count"
                type="number"
                onChange={handleColorCountInputChange}
                variant="outlined"
              />
            </div>
          )}
        </React.Fragment>
      ) : null}
    </React.Fragment>
  );
};

export default withSnackbar(CustomizationAreaForm);
